<template>
    <div class="page-main__section">
        <slot></slot>
        <div class="page-main__section-header col-12">
            <h2 class="page-main__section-title">
                Driver: {{ driver.name ? driver.name : 'New' }}
                <span v-if="isDefaultDriver"
                    class="element-badge is--active has--circle">
                    Default
                </span>
                <span v-if="!isDefaultDriver"
                    class="element-badge is--alt">
                    Not Default
                </span>
            </h2>
            <div class="page-main__buttons">
                <app-button v-if="!isDefaultDriver"
                    theme="secondary"
                    label="Set as Default"
                    @click="setDefaultDriver()">
                </app-button>
                <template v-if="!isEditing">
                    <app-button
                        theme="secondary"
                        label="Edit"
                        @click="isEditing = true">
                    </app-button>
                    <app-button v-if="!driver.projects?.length"
                        theme="icon"
                        @click="remove()">
                        <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                    </app-button>
                    <app-button v-else
                        theme="icon"
                        :disabled="true"
                        title="This driver cannot be removed, it is assigned to a project.">
                            <svg class="element-icon--delete element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-delete"></use></svg>
                    </app-button>
                </template>
            </div>
        </div>
        <div class="page-main__section-body">
            <div class="driver-dashboard-item__details grid">
                <div class="col-12 col-sm-6 col-md-3 col-lg-3">
                    <div class="input text">
                        <span class="label">Name</span>
                        <div ref="name"
                            v-html="sanitize(driver.name)"
                            class="input"
                            :contenteditable="isEditing"
                            title="Driver Name">
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-md-6 col-lg-7">
                    <div class="input text" v-if="driver.description || isEditing">
                        <span class="label">Description</span>
                        <div
                            v-html="sanitize(driver.description)"
                            class="input"
                            ref="description"
                            :contenteditable="isEditing"
                            title="Description">
                        </div>
                    </div>
                </div>
                <div class="col-12 col-sm-6 col-md-3 col-lg-2">
                    <div class="input text" v-if="driver.code || isEditing">
                        <span class="label">Code</span>
                        <div
                            v-html="sanitize(driver.code)"
                            class="input"
                            ref="code"
                            :contenteditable="isEditing"
                            title="Code">
                        </div>
                    </div>
                </div>
                <div class="driver-dashboard-item__private col-12">
                    <div class="input check">
                        <label :class="['element-toggle', isEditing ? '' : 'is--disabled']">
                            <input
                                type="checkbox"
                                class="element-toggle__toggle"
                                :id="`toggle-process-image-checkbox-${driver.id}`"
                                :disabled="!isEditing"
                                v-model="isPrivate"
                            />
                            <span class="label">Private</span>
                            <svg data-bs-toggle="dropdown" class="element-icon--tooltip-help element-icon" width="1em" height="1em"><use href="/img/icons.svg#icon-tooltip-help"></use></svg>
                            <div class="app-tooltip--private-driver-description app-tooltip dropdown-menu">
                                <h4 class="app-tooltip__title">Private</h4>
                                <div class="app-tooltip__body">Selecting the Private toggle causes this Driver to only be available for use with specific clients. When a new project is created, it will not be available in the overall list of Driver options unless the project is created under an included client.</div>
                            </div>
                        </label>
                    </div>
                    <div v-if="isPrivate" class="driver-dashboard-item__private-clients">
                        <app-select-multiple
                            label="Clients"
                            placeholder="Select clients..."
                            :choices="clientList"
                            :disabled="!isEditing"
                            :multiple="true"
                            v-model="selectedClientIds">
                        </app-select-multiple>
                    </div>
                </div>
                <div class="driver-dashboard-item__buttons page-main__buttons col-12">
                    <template v-if="isEditing">
                        <app-button
                            theme="secondary"
                            label="Cancel"
                            @click="cancel()">
                        </app-button>
                        <app-button
                            theme="primary"
                            label="Save"
                            @click="save()">
                        </app-button>
                    </template>
                </div>
            </div>

        <app-error
                v-if="hasErrors"
                class="is--inline"
                v-bind="errors" />

            <div v-if="source.id"
                class="driver-dashboard-item__used-versions">

                <div v-show="newVersion"
                    class="driver-dashboard-item__new-version grid">
                    <h2 class="page-main__section-title col-12">New Version</h2>
                    <div class="col-12 col-md-6 col-lg-7">
                        <div class="input textarea">
                            <span class="label">Description</span>
                            <textarea
                                v-if="newVersion"
                                v-model="newVersion.description"
                                placeholder="What has changed in this version?"
                                class="form-control mb-3"
                                :disabled="newVersionSaving"
                                name="description">
                            </textarea>
                        </div>
                    </div>
                    <div class="col-12 col-md-6 col-lg-5">
                        <div class="input inputfile">
                            <label class="inputfile__choose-file">
                                <div class="input file">
                                    <input type="file"
                                        ref="package"
                                        name="driver-file"
                                        :disabled="newVersionSaving"
                                        @change="onFileChange($event)"
                                        accept=".zip">
                                </div>
                            </label>
                        </div>
                    </div>
                    <div v-if="!newVersionSaving"
                        class="page-main__buttons col-12">
                        <app-button
                            theme="secondary"
                            label="Cancel"
                            @click="newVersion = false">
                        </app-button>
                        <app-button
                            theme="primary"
                            label="Save"
                            :disabled="!newVersionRequirementsMet"
                            @click="saveNewVersion()">
                        </app-button>
                    </div>
                    <div v-else
                        class="ml-auto">
                        <span 
                            title="Processing..."
                            class="element-badge">
                            Processing, please wait...
                        </span>
                    </div>
                </div>
                <div class="driver-dashboard-item__used-versions-header">
                    <driver-dashboard-cleanup-notification
                        v-if="driversWithUnusedVersions.includes(source.id)"
                        :driver="source">
                    </driver-dashboard-cleanup-notification>
                    <app-button
                        theme="secondary"
                        label="Manage Versions"
                        @click="viewVersions()">
                    </app-button>
                </div>
                <div class="element-table__wrapper">
                    <table v-if="versions.length"
                        class="driver-dashboard-item__versions-list col-12">
                        <thead>
                            <tr>
                                <th>Version ID</th>
                                <th>Description</th>
                                <th width="150px">Created Date</th>
                                <th width="150px">Uploaded By</th>
                                <th width="30%">Projects</th>
                            </tr>
                        </thead>
                        <tbody>
                            <driver-dashboard-version-item
                                v-for="version in sortedVersions"
                                :key="`version-${version.id}`"
                                :version="version"
                                :is-default-version="version.id === driver.default_version_id"
                                @set-default="setDefaultVersion">
                            </driver-dashboard-version-item>
                        </tbody>
                    </table>
                    <p v-else-if="versions.length == 0"
                        class="empty-table">
                        No versions in use.
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import DOMPurify from 'dompurify'
import api from '../api/drivers'
import DriverDashboardCleanupNotification from './DriverDashboardCleanupNotification.vue';
import DriverDashboardVersionItem from './DriverDashboardVersionItem'
import FileUploader from './FileUploader'

export default {
    components: {
        DriverDashboardVersionItem,
        FileUploader,
        DriverDashboardCleanupNotification
    },
    props: {
        disabled: {
            type: Boolean,
            default: false
        },
        clients: {
            type: Array,
            default: () => []
        },
        source: {
            type: Object,
            default: () => ({
                name: '',
                description: '',
                versions: []
            })
        },
        isDefaultDriver: {
            type: Boolean,
            default: false
        },
        driversWithUnusedVersions: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            driver: _.cloneDeep(this.source),
            isSaving: false,
            isEditing: this.source.id ? false : true,
            isPrivate: this.source.is_private === 1,
            selectedClientIds: (this.source.clients || []).map(client => String(client.id)),
            versions: this.source.versions || [],
            newVersion: false,
            newVersionSaving: false,
            errors: []
        }
    },
    computed: {
        sortedVersions () {
            return _.reverse(_.sortBy(this.versions, 'updated_at'))
        },
        requirementsMet () {
            return this.driver.name.length > 0
        },
        newVersionRequirementsMet () {
            return _.get(this.newVersion, 'description.length') > 0 &&
                   _.get(this.newVersion, 'package')
        },
        clientList () {
            return this.clients.map(client => ({
                id: String(client.id),
                text: client.name
            }))
        },
        hasErrors () {
            return this.errors?.message
        }
    },
    watch: {
        async isEditing (value) {
            if (value) {
                await this.$nextTick()
                _.result(this.$refs, 'name.focus')
            }
        },
        source: {
            deep: true,
            handler (value) {
                this.driver = _.cloneDeep(value)
                this.isPrivate = value.is_private === 1
                this.selectedClientIds = (value.clients || []).map(client => String(client.id))
            }
        },
        isPrivate (value) {
            if (!value) {
                this.selectedClientIds = []
            }
        }
    },
    methods: {
        cancel () {
            this.isEditing = false
            this.driver = _.cloneDeep(this.source)
            this.isPrivate = this.source.is_private === 1
            this.selectedClientIds = (this.source.clients || []).map(client => String(client.id))
            if (!this.driver.id) {
                this.$emit('cancel')
            }
        },
        async save () {
            this.isSaving = true

            const data = {
                name: this.getValue('name'),
                description: this.getValue('description'),
                code: this.getValue('code'),
                is_private: this.isPrivate ? 1 : 0,
                client_ids: this.isPrivate ? this.selectedClientIds.map(id => Number(id)) : []
            }

            // api call to save
            let result
            if (this.source.id) {
                result = await api.update(this.driver.id, data)
            } else {
                result = await api.create(data)
            }

            if (result.errors) {
                // catch errors
                this.errors = result
            } else {
                // append new driver to list
                this.driver = result
                this.isEditing = false
                this.$emit('update', result)
            }
            
            this.isSaving = false
        },
        async remove () {
            if (!confirm('Are you sure you want to remove this driver?')) {
                return
            }

            // remove driver locally
            this.$emit('remove', this.driver)

            // api call to save
            const result = await api.destroy(this.driver.id)

            if (result.errors) {
                // catch errors
                this.errors = result
                // put it back
                this.$emit('add', this.driver)
            }
        },
        async saveNewVersion () {
            this.newVersionSaving = true

            const formData = new FormData()
            formData.append('package', this.newVersion.package)
            formData.append('description', this.newVersion.description)

            // api call to save
            const result = await api.createVersion(this.driver.id, formData)
            console.log(result)

            if (result.errors) {
                // catch errors
                this.errors = result
            } else {
                // append new driver to list
                this.driver = result
                if (result.versions) {
                    this.versions = result.versions
                }
                // this.versions.push(result)
                this.newVersion = false
                this.$refs.package.value = ''
            }

            this.newVersionSaving = false
        },
        async setDefaultVersion (version) {
            if (!confirm('Are you sure you want to set this version as the default?')) {
                return
            }

            // api call to save
            const result = await api.setDefaultVersion(this.driver.id, version)

            if (result.errors) {
                // catch errors
                this.errors = result
            } else {
                this.driver = result
                if (this.driver.versions) {
                    this.versions = this.driver.versions
                }
            }
        },
        setDefaultDriver () {
            //this.driver = driver clicked
            this.$emit('set-default', this.driver)
        },
        createVersion () {
            this.newVersionSaving = false

            this.newVersion = {
                description: null,
                package: null
            }
        },
        getValue (name) {
            if (this.$refs[name]) {
                return this.$refs[name].textContent || this.$refs[name].innerText
            }
            return null
        },
        onFileChange (e) {
            this.newVersion.package = e.target.files[0]
        },
        viewVersions () {
            window.location.href = `versions/` + this.driver.id
        },
        sanitize (html) {
            return DOMPurify.sanitize(html || '')
        }
    },
    async created () {
        if (this.driver.id) {
            // this.setupEchoListener()
        }
        await this.$nextTick()
        _.result(this.$refs, 'name.focus')
    }
}
</script>
